import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../api';
import {
  Box,
  Button,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumb from '../components/Breadcrumb';
import GetAppIcon from '@mui/icons-material/GetApp';
import EditIcon from '@mui/icons-material/Edit';
import { formatDate } from '../utils/dateUtils';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { openOrDownloadPdf } from '../utils/generatePdf';
import StatusBadge from '../components/ui/StatusBadge';
import { pageContainerSx } from '../styles/tokens';
import { useToast } from '../components/ToastProvider';
import { useUser } from '../components/UserContext';
import BookingEditModal from '../components/BookingEditModal';

const labelSx = {
  fontSize: '0.8125rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'text.secondary',
  mb: 0.5,
  display: 'block',
};

const BookingDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useUser();
  const isWarehouse = (user?.role || '').toString().toLowerCase() === 'warehouse';
  const bookingsReturnPath = location.state?.fromBookings || '/bookings';

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusDialogStatus, setStatusDialogStatus] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);

  const goToBookings = () => navigate(bookingsReturnPath);

  const openChecklist = () => {
    if (!details?.id) return;
    navigate(`/bookings/${details.id}/checklist`, {
      state: { fromBookings: bookingsReturnPath },
    });
  };

  const allowedNextStatusesForWarehouse = (current) => {
    if (current === 'ORDERED') return ['DISPATCHED'];
    if (current === 'DISPATCHED') return ['RECEIVED'];
    return [];
  };

  const fetchDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/bookings/${id}`);
      setDetails(res.data);
      setStatusDialogStatus((res.data.status || '').toString().trim().toUpperCase());
    } catch (error) {
      showToast('Error loading booking details', 'error');
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
  }, [id]);

  // Sync dropdown from server when a booking first loads (so dropdown is never empty)
  useEffect(() => {
    if (details?.id != null) {
      const fromServer = (details.status || 'ORDERED').toString().trim().toUpperCase();
      setStatusDialogStatus(fromServer);
    }
  }, [details?.id]);

  const openBookingPdf = async (groupId) => {
    try {
      const res = await axios.get(`/api/bookings/${groupId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      openOrDownloadPdf(blob, `booking-${groupId}.pdf`);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to generate PDF', 'error');
    }
  };

  const openDeliveryChecklistPdf = async (groupId) => {
    try {
      const res = await axios.get(`/api/bookings/${groupId}/delivery-checklist-pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      openOrDownloadPdf(blob, `delivery-checklist-${groupId}.pdf`);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to generate Delivery Checklist PDF', 'error');
    }
  };

  const handleStatusSave = async () => {
    if (!details || !statusDialogStatus) return;
    const current = (details.status || '').toString().toUpperCase();
    if (statusDialogStatus === current) return;
    try {
      await axios.patch(`/api/bookings/${details.id}/status`, { status: statusDialogStatus });
      showToast('Status updated successfully', 'success');
      fetchDetails();
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to update status';
      showToast(msg, 'error');
    }
  };

  const handleEditSubmit = async (payload) => {
    try {
      await axios.patch(`/api/bookings/${details.id}`, payload);
      setEditModalOpen(false);
      fetchDetails();
      showToast('Booking updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update booking', 'error');
    }
  };

  if (loading) {
    return <PageLoader message="Loading booking details..." />;
  }

  if (!details) {
    return (
      <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', p: 3 }}>
        <Typography color="text.secondary">Booking not found.</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={goToBookings} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Bookings
        </Button>
      </Box>
    );
  }

  const transport = details.transportation_charges ?? 0;
  const finalPrice =
    details.final_price ??
    (details.total_amount ?? 0) + transport - (details.discount ?? 0);
  const remaining = finalPrice - (details.advance_payment ?? 0);
  const currentStatusNormalized = (details.status || 'ORDERED').toString().trim().toUpperCase();
  const statusChanged = statusDialogStatus && statusDialogStatus !== currentStatusNormalized;
  const products = details.products || [];
  // Always show a value: prefer user's selection, else current booking status (never empty when details loaded)
  const dropdownStatusValue = (statusDialogStatus || currentStatusNormalized).toString().trim().toUpperCase() || 'ORDERED';

  return (
    <Box sx={{ ...pageContainerSx, maxWidth: 720, px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <Breadcrumb items={[{ label: 'Bookings', path: bookingsReturnPath }, { label: `Order #${details.id}`, path: null }]} />
      <PageHeader
        title={`Booking #${details.id}`}
        subtitle={details.customer ? `${details.customer}${details.company_name ? ` · ${details.company_name}` : ''}` : 'Booking invoice and details'}
        actions={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <StatusBadge status={details.status} />
            {!isWarehouse && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                onClick={() => setEditModalOpen(true)}
                sx={{ textTransform: 'none', fontSize: '0.8125rem', borderRadius: 999, height: 40, px: 2.5 }}
              >
                Edit Booking
              </Button>
            )}
          </Box>
        )}
      />
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 }, '& > * + *': { mt: { xs: 2.5, sm: 3 } } }}>
          {/* Customer & Dates - stack on mobile, two cols on sm+ */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 2, sm: 3 } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={labelSx}>Customer</Typography>
              <Typography sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1rem' }}>
                {details.customer ?? '—'}
              </Typography>
              {(details.company_name != null && details.company_name !== '') && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {details.company_name}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 }}>
              <Box>
                <Typography sx={labelSx}>Booking</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.primary' }}>
                  {formatDate(details.booking_date)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Dispatch</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.primary' }}>
                  {formatDate(details.dispatch_date)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={labelSx}>Return</Typography>
                <Typography sx={{ fontWeight: 500, fontSize: '0.75rem', color: 'text.primary' }}>
                  {formatDate(details.return_date) || '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Products - cards on mobile, table on desktop */}
          <Box>
            <Typography sx={{ ...labelSx, mb: 1 }}>Products</Typography>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {products.map((p, i) => (
                  <Paper key={i} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>{p.product_name ?? '—'}</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                          #{i + 1} · Qty {p.quantity}
                          {!isWarehouse && ` · ₹${(p.rate ?? 0).toFixed(2)}/unit`}
                        </Typography>
                      </Box>
                      {!isWarehouse && (
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                          ₹{(p.amount ?? (p.rate ?? 0) * (p.quantity ?? 0)).toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider', overflowX: 'auto', overflowY: 'hidden' }}>
                <Table size="small" sx={{ minWidth: 360 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'action.hover' }}>
                      <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 40, minWidth: 40, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>#</TableCell>
                      <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, minWidth: 120, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Product</TableCell>
                      <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 48, minWidth: 48, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Qty</TableCell>
                      {!isWarehouse && (
                        <>
                          <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 80, minWidth: 80, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Rate</TableCell>
                          <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, width: 80, minWidth: 80, fontSize: '0.8125rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Amount</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {products.map((p, i) => (
                      <TableRow key={i} sx={{ '&:last-child td': { borderBottom: 0 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.secondary' }}>{i + 1}</TableCell>
                        <TableCell sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, minWidth: 120, fontSize: '1rem', color: 'text.primary' }}>{p.product_name ?? '—'}</TableCell>
                        <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.primary' }}>{p.quantity}</TableCell>
                        {!isWarehouse && (
                          <>
                            <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.secondary' }}>₹{(p.rate ?? 0).toFixed(2)}</TableCell>
                            <TableCell align="right" sx={{ px: { xs: 1.5, sm: 2 }, py: 1.25, fontSize: '1rem', color: 'text.primary' }}>₹{(p.amount ?? (p.rate ?? 0) * (p.quantity ?? 0)).toFixed(2)}</TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Pricing Summary - hidden for warehouse */}
          {!isWarehouse && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: { xs: '100%', sm: 288 }, fontSize: '1rem' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  { label: 'Total Amount', value: details.total_amount ?? 0 },
                  { label: 'Transportation Charges', value: transport },
                  { label: 'Discount', value: details.discount ?? 0 },
                  { label: 'Final Price', value: finalPrice },
                  { label: 'Advance Payment', value: details.advance_payment ?? 0 },
                ].map((row) => (
                  <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75 }}>
                    <Typography component="span" sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                      {row.label}
                    </Typography>
                    <Typography component="span" sx={{ fontSize: '1rem', color: 'text.primary' }}>
                      ₹{Number(row.value).toFixed(2)}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 1.5,
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                    Remaining Balance
                  </Typography>
                  <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                    ₹{remaining.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          )}

          {/* Actions - stack on mobile, row on sm+; buttons flex on mobile */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'flex-end' },
              justifyContent: 'space-between',
              gap: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              pt: 2.5,
            }}
          >
            <Box>
              <Typography component="label" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mb: 1 }}>
                Update Status
              </Typography>
              <Box
                component="select"
                value={dropdownStatusValue}
                onChange={(e) => {
                  const next = (e.target.value || '').toString().trim().toUpperCase();
                  setStatusDialogStatus(next);
                }}
                sx={{
                  width: { xs: '100%', sm: 160 },
                  minWidth: { xs: 0, sm: 160 },
                  height: 32,
                  px: 1.5,
                  fontSize: '0.75rem',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  cursor: 'pointer',
                  '&:focus': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                }}
              >
                {!isWarehouse ? (
                  <>
                    <option value="ORDERED">ORDERED</option>
                    <option value="DISPATCHED">DISPATCHED</option>
                    <option value="RECEIVED">RECEIVED</option>
                    <option value="CANCELLED">CANCELLED</option>
                  </>
                ) : (
                  (() => {
                    const current = (details?.status || 'ORDERED').toString().toUpperCase();
                    const next = allowedNextStatusesForWarehouse(current);
                    const options = [current, ...next.filter((s) => s !== current)];
                    return options.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ));
                  })()
                )}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                '& .MuiButton-root': {
                  flex: { xs: 1, sm: 'none' },
                  minWidth: { xs: 0, sm: 'auto' },
                  textTransform: 'none',
                  fontSize: '0.75rem',
                  minHeight: 32,
                  py: 0.75,
                  px: 1.5,
                },
              }}
            >
              {!isWarehouse && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GetAppIcon sx={{ fontSize: 14 }} />}
                  onClick={() => openBookingPdf(details.id)}
                >
                  Invoice
                </Button>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={openChecklist}
              >
                Open Checklist
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<GetAppIcon sx={{ fontSize: 14 }} />}
                onClick={() => openDeliveryChecklistPdf(details.id)}
              >
                Checklist
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleStatusSave}
                disabled={!statusChanged}
              >
                Save Status
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      <BookingEditModal
        isOpen={editModalOpen}
        booking={details}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleEditSubmit}
      />
    </Box>
  );
};

export default BookingDetails;
